import { useId, useState } from 'react';
import { endsNextDay, type EventWhen } from './eventWhen';

export interface EventWhenFieldsProps {
  value: EventWhen;
  onChange: (next: EventWhen) => void;
  /** Focus the date input on mount. */
  autoFocus?: boolean;
}

const input =
  'w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Date, an "All day" switch, start and end time, and an optional last day for
 * an event that runs several days. Used by the New Event form and the event
 * header's date editor, so both speak about an event's time the same way.
 */
export function EventWhenFields({ value, onChange, autoFocus }: EventWhenFieldsProps) {
  const id = useId();
  const set = (patch: Partial<EventWhen>) => onChange({ ...value, ...patch });
  // Its own state, so clearing the last day while retyping it keeps the field.
  const [multiDay, setMultiDay] = useState(value.endDate !== '');

  return (
    <div className="space-y-3">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label htmlFor={`${id}-date`} className="block text-sm font-medium text-gray-700 mb-1">
            {multiDay ? 'First day' : 'Date'}
          </label>
          <input
            id={`${id}-date`}
            type="date"
            value={value.date}
            autoFocus={autoFocus}
            onChange={(e) => set({ date: e.target.value })}
            className={input}
          />
        </div>
        {multiDay && (
          <div className="flex-1">
            <label
              htmlFor={`${id}-end-date`}
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Last day
            </label>
            <input
              id={`${id}-end-date`}
              type="date"
              value={value.endDate}
              min={value.date || undefined}
              onChange={(e) => set({ endDate: e.target.value })}
              className={input}
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            role="switch"
            checked={value.allDay}
            onChange={(e) => set({ allDay: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          All day
        </label>
        <button
          type="button"
          onClick={() => {
            set({ endDate: multiDay ? '' : value.date });
            setMultiDay(!multiDay);
          }}
          className="text-sm text-primary-700 hover:underline"
        >
          {multiDay ? 'One day only' : 'Runs several days'}
        </button>
      </div>

      {!value.allDay && (
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label htmlFor={`${id}-start`} className="block text-sm font-medium text-gray-700 mb-1">
              Starts
            </label>
            <input
              id={`${id}-start`}
              type="time"
              value={value.startTime}
              onChange={(e) => set({ startTime: e.target.value })}
              className={input}
            />
          </div>
          <div className="flex-1">
            <label htmlFor={`${id}-end`} className="block text-sm font-medium text-gray-700 mb-1">
              Ends <span className="text-xs text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id={`${id}-end`}
              type="time"
              value={value.endTime}
              onChange={(e) => set({ endTime: e.target.value })}
              className={input}
            />
          </div>
        </div>
      )}
      {endsNextDay(value) && (
        <p className="text-xs text-gray-500">Ends after midnight, on the next day.</p>
      )}
    </div>
  );
}
